import type { EmailHealthResponse } from '../types/emailHealth';

/**
 * Pure function — no side effects.
 * Converts an EmailHealthResponse into a Markdown diagnostic report
 * suitable for pasting into GitHub Copilot.
 */
export function buildMarkdownReport(health: EmailHealthResponse): string {
  const statusEmoji =
    health.status === 'healthy' ? '✅' :
    health.status === 'degraded' ? '⚠️' : '🔴';

  const lines: string[] = [
    `## CLEAR Portal Email Health Report`,
    ``,
    `**Status:** ${statusEmoji} ${health.status.toUpperCase()}`,
    `**Generated:** ${health.generatedAt}`,
    ``,
    `### SMTP`,
    `- **Reachable:** ${health.smtp.reachable ? 'Yes' : 'No'}`,
  ];

  if (health.smtp.error) {
    lines.push(`- **Error:** ${health.smtp.error}`);
  }

  const lastSentLabel = health.rolling24h.lastSentAt
    ? new Date(health.rolling24h.lastSentAt).toLocaleString()
    : 'No emails in 24h';

  lines.push(
    ``,
    `### 24-Hour Stats`,
    `| Metric | Value |`,
    `|--------|-------|`,
    `| Sent | ${health.rolling24h.sent} |`,
    `| Failed | ${health.rolling24h.failed} |`,
    `| Failure Rate | ${(health.rolling24h.failureRate * 100).toFixed(1)}% |`,
    `| Last Sent | ${lastSentLabel} |`,
    ``,
    `### By Email Type`,
  );

  // Prefer the multi-window data (24h window) if available, fall back to legacy byType
  const byTypeRows = health.byTypeWindows ? health.byTypeWindows['24h'] : health.byType;

  if (byTypeRows.length === 0) {
    lines.push(`No email types recorded in the last 24 hours.`);
  } else {
    lines.push(`| Email Type | Count |`, `|------------|-------|`);
    for (const t of byTypeRows) {
      lines.push(`| ${t.emailType} | ${t.count} |`);
    }
  }

  lines.push(``, `### Reminder Config Last Runs`);

  if (health.reminderConfigs.length === 0) {
    lines.push(`No reminder configs active.`);
  } else {
    lines.push(
      `| Feature | Chapter | Last Run | Sent | Failed | Error |`,
      `|---------|---------|----------|------|--------|-------|`,
    );
    for (const c of health.reminderConfigs) {
      const lastRun = c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : 'Never';
      lines.push(
        `| ${c.feature} | ${c.chapterName ?? c.chapterId} | ${lastRun} | ${c.lastRunSentCount ?? 0} | ${c.lastRunFailedCount ?? 0} | ${c.lastRunErrorSummary ?? ''} |`,
      );
    }
  }

  lines.push(
    ``,
    `### CloudWatch Alarm`,
    `- **State:** ${health.alarmState ?? 'Unknown (CloudWatch unreachable)'}`,
    ``,
    `---`,
    `*Please help me diagnose and fix this email issue.*`,
  );

  return lines.join('\n');
}
